import { message } from 'antd';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { FarmData } from 'types/FarmData';
import useQueryApiClient from '../utils/useQueryApiClient';
import { AxiosResponse } from 'axios';
import readHeader from '../utils/readHeader';
import { fetchCsrfCookie } from '../utils/csrfCookie';
import { useNavigate } from 'react-router-dom';
import { routes } from 'config/config';
import PublicRoute from 'enums/publicRoutes';
import { useIdleLogout } from './useIdleLogout';

export interface AuthProps {
  name?: string;
  surname?: string;
  authenticated: boolean;
  farmId?: number;
  isAdmin?: boolean;
  isFarmOwner?: boolean;
  isMansLauksLinked?: boolean;
  hasMansLauksToken?: boolean;
  regNr?: number;
  email?: string;
  confirmed_at?: string;
  abilities?: string[];
  farmName?: string;
  role?: string;
  farm?: FarmData;
  sessionIdleMinutes?: number;
}

interface ErrorResponse {
  error: string;
}

export type Ability =
  | 'view_company'
  | 'edit_company'
  | 'view_transaction'
  | 'edit_transaction'
  | 'view_contract'
  | 'edit_contract'
  | 'view_delivery'
  | 'edit_delivery'
  | 'view_product'
  | 'edit_product'
  | 'view_coupon'
  | 'edit_coupon'
  | 'view_quality_mark'
  | 'edit_quality_mark'
  | 'view_partner'
  | 'edit_partner'
  | 'view_farm_user'
  | 'edit_farm_user'
  | 'view_farm_listing'
  | 'edit_farm_listing';

interface AuthContextProps {
  user: AuthProps | null;
  isFetchingUser: boolean;
  login: (formData: any) => Promise<void>;
  logout: () => Promise<void>;
  refetchUser: () => void;
  verifySession: () => void;
  isLoginLoading: boolean;
  authenticate: (userData: AuthProps) => void;
  farms: FarmData[];
  refetchFarms: () => void;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  isOpenModal: boolean;
  resendEmail: any;
  cooldown: number;
  pendingEmail: string | null;
  can: (permission: Ability) => boolean | undefined;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [user, setUser] = useState<AuthProps | null>(null);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
  const comesFrom = localStorage.getItem('comesFrom');
  const intl = useIntl();
  const navigate = useNavigate();

  useIdleLogout(!!user?.authenticated, user?.sessionIdleMinutes);

  useEffect(() => {
    const until = Number(sessionStorage.getItem('cdUntil'));
    if (until && until > Date.now()) {
      setCooldown(Math.ceil((until - Date.now()) / 1000));
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;

    const id = setInterval(() => {
      setCooldown((sec) => {
        if (sec <= 1) {
          clearInterval(id);
          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [cooldown]);

  const { data: farms, refetch: refetchFarms } = useQueryApiClient({
    request: {
      url: `/api/v2/user/farms`,
    },
    enabled: !!user?.authenticated,
  });

  const { refetch: fetchUser } = useQueryApiClient({
    request: {
      url: `/api/v2/user`,
      method: 'GET',
    },
    onSuccess: (data: AuthProps) => {
      setUser(data);

      if (data.authenticated) {
        localStorage.setItem('wasLoggedIn', '1');
      }

      if (
        data['isFarmOwner'] &&
        !data['confirmed_at'] &&
        !window.location.pathname.startsWith(PublicRoute.APPROVE_REGISTRATION)
      ) {
        navigate(PublicRoute.EMAIL_CONFIRM);
      }
      setIsFetchingUser(false);
    },
    onError: () => {
      setUser({ authenticated: false });
      setIsFetchingUser(false);
      sessionStorage.removeItem('processPurchaseFormValues');
    },
    enabled: true,
  });

  const handleExpiredSession = (isLatvijaLv: boolean) => {
    setUser({ authenticated: false });

    if (localStorage.getItem('wasLoggedIn') === '1') {
      localStorage.removeItem('wasLoggedIn');
      sessionStorage.setItem('pendingLoginError', 'message.session_expired');

      if (isLatvijaLv) {
        window.location.replace(routes.api.baseUrl + '/vpm/logout');
      } else {
        window.location.href = PublicRoute.LOGIN;
      }
    }
  };

  const { refetch: verifySession } = useQueryApiClient({
    request: {
      url: `/api/v2/user`,
      method: 'GET',
      disableOnMount: true,
      errorWithHeaders: true,
    },
    onSuccess: (data: AuthProps) => {
      if (!data.authenticated) {
        handleExpiredSession(false);
      }
    },
    onError: (response: AxiosResponse) => {
      if (response?.status === 401) {
        handleExpiredSession(!!(response?.data as any)?.latvija_lv);
      }
    },
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `/api/v2/login`,
      method: 'POST',
    },
    onSuccess: (response) => {
      setUser({ authenticated: response.authenticated } as AuthProps);
      fetchUser();
      if (comesFrom) {
        window.location.href = `/${comesFrom}`;
        localStorage.removeItem('comesFrom');
      } else {
        window.location.href = PublicRoute.HOME;
      }
    },
    onError: (response) => {
      handleErrorResponse(response);
    },
    enabled: true,
  });

  const { appendData: logoutRequest, isLoading: isLoginLoading } =
    useQueryApiClient({
      request: {
        url: `/api/v2/logout`,
        method: 'POST',
      },
      onSuccess: (response) => {
        setUser({ authenticated: false });
        sessionStorage.removeItem('processPurchaseFormValues');

        ['_WHOLESALE', '_EMARKET'].forEach((suffix) => {
          [
            'farmProducts',
            'priceTotal',
            'coupons',
            'cartId',
            'cartName',
          ].forEach((key) => {
            localStorage.removeItem(`${key}${suffix}`);
          });
        });
        localStorage.removeItem('wholesaleDeliveryMode');
        localStorage.removeItem('wholesaleDeliveries');
        localStorage.removeItem('wasLoggedIn');

        if (response?.is_latvija_lv_user) {
          setTimeout(() => {
            window.location.replace(routes.api.baseUrl + '/vpm/logout');
          }, 0);
        } else {
          window.location.href = PublicRoute.LOGIN;
        }
      },
      enabled: true,
    });

  const { appendData: resendEmail } = useQueryApiClient({
    request: {
      url: '/api/v2/resend-email',
      method: 'POST',
      errorWithHeaders: true,
    },
    onSuccess: () => handleSuccessResend(),
    onError: (response: AxiosResponse) => handleResendEmailError(response),
  });

  const login = async (formData: any) => {
    try {
      await fetchCsrfCookie();
      setPendingEmail(formData.email);
      appendData(formData);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = async () => {
    try {
      logoutRequest();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleErrorResponse = (response: ErrorResponse) => {
    if (response.error === 'verify_email') {
      setIsOpenModal(true);
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    } else {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    }
  };

  const handleSuccessResend = () => {
    setPendingEmail(null);
    setIsOpenModal(false);
    message.success(intl.formatMessage({ id: 'login.resend_success' }));
  };

  const can = (permission: Ability) => {
    if (user?.isAdmin) {
      return true;
    }

    return user?.abilities?.includes(permission);
  };

  const handleResendEmailError = (res: AxiosResponse) => {
    const { status, data } = res;

    switch (status) {
      case 429: {
        const retryAfter = Number(readHeader(res, 'retry-after') ?? 60);
        const until = Date.now() + retryAfter * 1000;

        sessionStorage.setItem('cdUntil', String(until));
        setCooldown(retryAfter);

        message.warning(
          intl.formatMessage({ id: 'login.email_already_sent_msg' })
        );
        break;
      }
      case 404: {
        message.error(intl.formatMessage({ id: `login.${data.error}` }));
        break;
      }
      case 409: {
        if (data?.message === 'already_verified') {
          message.info(intl.formatMessage({ id: `login.${data.message}` }));
        }
        break;
      }
      default: {
        const msgId = data?.error ?? 'message.unknown_error';
        message.error(intl.formatMessage({ id: msgId }));
      }
    }
  };

  const refetchUser = () => fetchUser();

  const authenticate = (userData: AuthProps) => {
    setUser(userData);
    fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isFetchingUser,
        login,
        logout,
        refetchUser,
        verifySession,
        isLoginLoading,
        authenticate,
        farms,
        refetchFarms,
        setIsOpenModal,
        isOpenModal,
        resendEmail,
        pendingEmail,
        cooldown,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
